/**
 * Shared numeric formatting core for the app's masked inputs.
 *
 * The app targets the European market: every numeric quantity is grouped in the
 * `de-DE` convention (`.` thousands separator, `,` decimal separator). Both the
 * monetary {@link CurrencyInput} and the unit-suffixed `NumberInput` build on
 * this module so there is a single locale and a single caret implementation
 * rather than two divergent forks.
 */
export const NUMBER_LOCALE = 'de-DE';

export const DECIMAL_SEPARATOR = ',';

const integerFormatter = new Intl.NumberFormat(NUMBER_LOCALE, {
  useGrouping: true,
  maximumFractionDigits: 0,
});

// Ungrouped integer formatter for ordinal values (e.g. a year) that read as a
// label, not a quantity: a year must never carry a thousands separator (`1904`,
// not `1.904`).
const plainIntegerFormatter = new Intl.NumberFormat(NUMBER_LOCALE, {
  useGrouping: false,
  maximumFractionDigits: 0,
});

/**
 * Counts the digits in a string up to (not including) the given caret offset.
 * Used to restore the caret after re-grouping: the digit index is stable even
 * when separators shift around it, so the caret tracks the same digit boundary
 * rather than jumping to the end. Decimal separators count as non-digits, so a
 * grouped decimal value is handled identically to an integer one.
 */
export function digitsBeforeCaret(raw: string, caret: number): number {
  let count = 0;

  for (let index = 0; index < caret && index < raw.length; index += 1) {
    if (/\d/.test(raw[index] as string)) count += 1;
  }

  return count;
}

/**
 * Maps a digit index back to a string offset in the formatted value: the
 * position just after the Nth digit. Used together with
 * {@link digitsBeforeCaret} to re-place the caret after re-grouping.
 */
export function caretForDigitIndex(formatted: string, digitIndex: number): number {
  if (digitIndex <= 0) return 0;

  let seen = 0;

  for (let index = 0; index < formatted.length; index += 1) {
    if (/\d/.test(formatted[index] as string)) {
      seen += 1;

      if (seen === digitIndex) return index + 1;
    }
  }

  return formatted.length;
}

/**
 * Maps a digit index to the string offset just after the decimal separator in
 * a formatted value. Used when the user has just typed a separator: the caret
 * must land in the fractional part rather than stalling before the separator
 * (where the next keystroke would be misread as another integer digit).
 */
export function caretAfterDecimalSeparator(formatted: string, integerDigitIndex: number): number {
  const separatorOffset = formatted.indexOf(DECIMAL_SEPARATOR);

  if (separatorOffset === -1) return caretForDigitIndex(formatted, integerDigitIndex);

  return separatorOffset + 1;
}

/**
 * Formats an integer digit string with `de-DE` thousands separators. An empty
 * string stays empty (so a cleared field never renders `0`); leading zeros are
 * normalised away by the numeric round-trip. Pass `useGrouping: false` for
 * ordinal values such as years, which must render bare.
 */
export function formatIntegerDigits(digits: string, useGrouping = true): string {
  if (digits === '') return '';

  const value = Number(digits);

  if (!Number.isFinite(value)) return '';

  return (useGrouping ? integerFormatter : plainIntegerFormatter).format(value);
}

/**
 * Splits a raw integer-mode input into its kept digits. Strips every
 * non-digit character (separators, letters, a leading `-`), so a negative or
 * decimal entry collapses to its bare digits — counts are non-negative whole
 * numbers.
 */
export function integerDigitsOnly(raw: string): string {
  return raw.replace(/\D/g, '');
}

type DecimalParts = {
  readonly integerDigits: string;
  readonly fractionDigits: string;
  readonly hasSeparator: boolean;
};

/**
 * Reduces a raw decimal-mode input to its meaningful parts: the integer
 * digits, the fractional digits (capped at `maxFractionDigits`), and whether a
 * decimal separator was typed. Only `,` is the de-DE decimal separator; `.` is
 * the grouping separator (the live mask re-inserts it), so every `.`, letter,
 * and leading `-` is stripped — quantities are non-negative. Only the first `,`
 * counts; later ones are dropped.
 */
export function decimalParts(raw: string, maxFractionDigits: number): DecimalParts {
  const withoutGrouping = raw.replace(new RegExp(`[^0-9${DECIMAL_SEPARATOR}]`, 'g'), '');

  let integerDigits = '';
  let fractionDigits = '';
  let hasSeparator = false;

  for (const char of withoutGrouping) {
    if (char === DECIMAL_SEPARATOR) {
      if (hasSeparator) continue;

      hasSeparator = true;

      continue;
    }

    if (hasSeparator) {
      if (fractionDigits.length < maxFractionDigits) fractionDigits += char;
    } else {
      integerDigits += char;
    }
  }

  return { integerDigits, fractionDigits, hasSeparator };
}

/**
 * Formats a raw decimal-mode input into its grouped display string, preserving
 * a trailing separator and any fractional digits the user is mid-typing. An
 * empty entry stays empty so a cleared field never renders `0`.
 */
export function formatDecimalInput(raw: string, maxFractionDigits: number): string {
  const { integerDigits, fractionDigits, hasSeparator } = decimalParts(raw, maxFractionDigits);

  if (integerDigits === '' && fractionDigits === '' && !hasSeparator) return '';

  const groupedInteger = integerDigits === '' ? '0' : integerFormatter.format(Number(integerDigits));

  if (!hasSeparator) return groupedInteger;

  return `${groupedInteger}${DECIMAL_SEPARATOR}${fractionDigits}`;
}

/**
 * Parses a raw integer-mode input to its clean numeric value, or `null` when no
 * digits were entered. Never coerces an empty field to `0`.
 */
export function parseIntegerInput(raw: string): number | null {
  const digits = integerDigitsOnly(raw);

  if (digits === '') return null;

  return Number(digits);
}

/**
 * Parses a raw decimal-mode input to its clean numeric value, or `null` when no
 * digits were entered. The mask is presentation only, so the returned value
 * carries no separators or unit. Never coerces an empty field to `0`.
 */
export function parseDecimalInput(raw: string, maxFractionDigits: number): number | null {
  const { integerDigits, fractionDigits } = decimalParts(raw, maxFractionDigits);

  if (integerDigits === '' && fractionDigits === '') return null;

  const normalised = `${integerDigits === '' ? '0' : integerDigits}.${fractionDigits === '' ? '0' : fractionDigits}`;
  const value = Number(normalised);

  return Number.isFinite(value) ? value : null;
}

/**
 * Formats a stored integer value for display on mount. `null` renders empty,
 * distinguishing "no value" from `0`. Pass `useGrouping: false` for ordinal
 * values such as years, which must render bare.
 */
export function formatStoredInteger(value: number | null, useGrouping = true): string {
  if (value === null) return '';

  return (useGrouping ? integerFormatter : plainIntegerFormatter).format(Math.trunc(value));
}

/**
 * Formats a stored decimal value for display on mount, rounded to
 * `maxFractionDigits` and stripped of trailing zeros so `85.5` renders `85,5`
 * rather than `85,50`. `null` renders empty, distinguishing "no value" from
 * `0`.
 */
export function formatStoredDecimal(value: number | null, maxFractionDigits: number): string {
  if (value === null) return '';

  const formatter = new Intl.NumberFormat(NUMBER_LOCALE, {
    useGrouping: true,
    maximumFractionDigits: maxFractionDigits,
  });

  return formatter.format(value);
}
